import { cleanPreviewText, formatDate, formatLevel, sleep } from "./format";
import type {
  AuthoritativeConcept,
  ChangeItem,
  CompiledDocument,
  DocumentSection,
  RunForm,
  RunStatus,
  RunState,
  StageItem
} from "./types";

/**
 * Pure run reducers and local-preview run construction.
 *
 * Everything here must work with no backend: `apiUrl === ""` is a supported runtime, and
 * these functions are what make the whole app usable in local preview.
 */

export const initialForm: RunForm = {
  documentId: "atlas-dpi-v2",
  organization: "",
  orgWebsite: "",
  recipientName: "",
  recipientRole: "Head of Digital Health",
  recipientUrl: "",
  purpose: "exploratory_funding",
  fundingCall: "",
  relationshipContext: "",
  notes: "",
  mode: "full_diligence",
  personalizationLevel: "L2",
  allowSubstantive: false
};

export const stageTemplate: StageItem[] = [
  { label: "Input snapshot", state: "done" },
  { label: "Target evidence", state: "done" },
  { label: "Protected facts", state: "done" },
  { label: "Document draft", state: "active" },
  { label: "Layout check", state: "waiting" },
  { label: "Approval", state: "waiting" }
];

export function isInternalRun(run: RunStatus) {
  const label = `${run.runId} ${run.input.organization} ${run.document.title}`.toLowerCase();
  return /\b(smoke|qa|test|check|vertex|gemini|bedrock|highest model|break|stress|fixture|sample|demo)\b|breaktest|meridianbreak/.test(label);
}

export function approvalSummary(run: RunStatus) {
  const dateSource = run.qaLoop?.completedAt || run.document.updatedAt;
  if (run.status === "approved") {
    return {
      label: run.qaLoop?.aligned ? "Approved by QA" : "Approved",
      dateLabel: "Approved",
      date: formatDate(dateSource)
    };
  }
  if (run.status === "blocked_for_human" || run.qaLoop?.aligned === false) {
    return {
      label: "QA blocked",
      dateLabel: "Last QA",
      date: formatDate(dateSource)
    };
  }
  return {
    label: "Needs review",
    dateLabel: "Updated",
    date: formatDate(dateSource)
  };
}

export function documentPreview(run: RunStatus) {
  const firstEditable = run.document.sections.find((section) => !section.locked) || run.document.sections[0];
  const excerpt = cleanPreviewText(firstEditable?.body || run.message);
  return {
    title: run.document.title || `${run.input.organization} audience draft`,
    subtitle: run.document.subtitle || "ATLAS DPI Concept Note",
    excerpt
  };
}

export function decisionSummary(change: ChangeItem) {
  if (change.verdict === "approved") return "Will enter final copy";
  if (change.verdict === "rejected") return "Excluded from final copy";
  return "Needs your decision";
}

/**
 * THE OPENING SENTENCE OF THE SOURCE, RESOLVED IN EXACTLY ONE PLACE.
 *
 * Two surfaces need it and they may never disagree: buildRecommendedEditPlan() takes it as the
 * `before` of the first proposed edit, and /new's document card renders it as the note's opening
 * prose. They used to resolve it separately — the plan probed the concept text and fell back to a
 * declared sentence, while the card probed the same text and fell back to `textExcerpt`, which is
 * a METADATA BLURB ("Packaged ATLAS DPI v2 source and approved baseline package."). With the
 * packaged canonical source, both probes miss, so the card showed a caption dressed as document
 * prose while the diff two panels away showed the real sentence.
 *
 * One helper, one answer. If it ever returns "", the card renders NO excerpt — an empty page body
 * is honest; a blurb presented as the document's opening is not.
 */
export function resolveOpeningSentence(concept: Pick<AuthoritativeConcept, "text" | "textExcerpt">) {
  const sourceText = `${concept.text || ""}\n${concept.textExcerpt || ""}`;
  return (
    findSourceSentence(sourceText, "ATLAS is a sovereign evaluation infrastructure") ||
    findSourceSentence(sourceText, "Medical AI is transitioning") ||
    "ATLAS is presented as a sovereign medical AI evaluation initiative."
  );
}

/**
 * THE AUDIENCE-FIT SENTENCE, RESOLVED IN EXACTLY ONE PLACE — the same contract as
 * resolveOpeningSentence() above, for the same reason.
 *
 * This is the sentence buildRecommendedEditPlan() takes as the `before` of the audience-fit edit,
 * and it is the second paragraph /new's document card renders. With the PACKAGED canonical source
 * there is no `text` at all (the concept carries only a metadata `textExcerpt`), so probing the
 * source for "catalytic financing" returns "" — which is exactly why the card's second paragraph
 * rendered NOTHING at every width while the diff below it showed the declared fallback sentence.
 * The two surfaces were reading the same thing through two different call paths and disagreeing.
 *
 * One helper, one answer, so the page and the diff can never diverge: the card now shows the SAME
 * string the compiler is actually working from, whether that comes from the probe or from the
 * declared source line. Nothing is invented — no filler was written for the page; this is the
 * plan's own `before`.
 */
export function resolveAudienceFitSentence(concept: Pick<AuthoritativeConcept, "text" | "textExcerpt">) {
  const sourceText = `${concept.text || ""}\n${concept.textExcerpt || ""}`;
  return (
    findSourceSentence(sourceText, "catalytic financing") ||
    "The note describes the ATLAS funding request, evidence case, operating model, and next steps."
  );
}

export function buildRecommendedEditPlan(form: RunForm, concept: AuthoritativeConcept): ChangeItem[] {
  const organization = form.organization.trim() || "the partner";
  const role = form.recipientRole.trim() || "the recipient team";
  const sourceText = `${concept.text || ""}\n${concept.textExcerpt || ""}`;
  const openingBefore = resolveOpeningSentence(concept);
  const fitBefore = resolveAudienceFitSentence(concept);
  const nextStepBefore =
    findSourceSentence(sourceText, "exploratory conversation") ||
    "The closing asks for an exploratory conversation about catalytic financing.";

  return [
    {
      id: "opening-frame",
      page: "Page 2",
      title: "Opening frame",
      before: openingBefore,
      after: `For ${organization}, ATLAS should open as a governed medical AI evaluation initiative: independent, evidence-led, and designed to help ${role} assess clinical safety infrastructure without changing the core proposal.`,
      reason: `Makes the first substantive paragraph speak to ${organization} while preserving the ATLAS premise and avoiding any implied endorsement.`,
      verdict: "pending"
    },
    {
      id: "audience-fit",
      page: "Page 7",
      title: "Audience fit",
      before: fitBefore,
      after: `The audience-fit language should foreground ${organization}'s likely interest in trustworthy evidence, accountable AI governance, and implementation discipline, while keeping funding amounts, country-node claims, and certainty language unchanged.`,
      reason: "Shows the reader why this version is for them without altering protected facts or over-claiming the relationship.",
      verdict: "pending"
    },
    {
      id: "next-step",
      page: "Page 31",
      title: "Next step",
      before: nextStepBefore,
      after: `The closing should propose a focused exploratory conversation with ${organization} on catalytic financing, clinical safety evidence, and non-extractive governance for medical AI evaluation.`,
      reason: "Turns the close into a specific next action while staying exploratory and non-committal.",
      verdict: "pending"
    }
  ];
}

/**
 * SAFETY: an undecided edit must never reach the final copy. Anything still "pending" when
 * the document is created is coerced to "rejected" — the conservative direction. Never
 * change this to default a pending edit into "approved".
 */
export function finalizePlannedChanges(changes: ChangeItem[] = []) {
  return changes.map((change) => ({
    ...change,
    verdict: change.verdict === "pending" ? "rejected" as const : change.verdict
  }));
}

export function findSourceSentence(sourceText: string, needle: string) {
  const normalized = sourceText.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const index = normalized.toLowerCase().indexOf(needle.toLowerCase());
  if (index < 0) return "";
  const start = Math.max(0, normalized.lastIndexOf(".", index - 2) + 1);
  const endIndex = normalized.indexOf(".", index + needle.length);
  const end = endIndex > index ? endIndex + 1 : Math.min(normalized.length, index + 260);
  return cleanPreviewText(normalized.slice(start, end).trim());
}

export async function createLocalRun(form: RunForm, status: RunState = "needs_review") {
  await sleep(350);
  return createDemoRun(form, status);
}

export function createDemoRun(form: RunForm, status: RunState = "needs_review"): RunStatus {
  const slug = form.organization
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 24);
  const runId = `compiler_${new Date().toISOString().slice(0, 10).replace(/-/g, "_")}_${slug || "target"}`;
  const document = createDocument(form);

  return {
    runId,
    status,
    etaMinutes: form.mode === "draft" ? 8 : 34,
    fitScore: form.personalizationLevel === "L0" ? 3.8 : 4.2,
    warnings: form.allowSubstantive ? ["Substantive edits require individual approval."] : [],
    changes: createChanges(form),
    stages: stageTemplate,
    message: `${form.organization} draft ready with ${formatLevel(form.personalizationLevel)} controls.`,
    input: form,
    document,
    promptHistory: [],
    model: { id: "local-preview", used: false }
  };
}

export function createDocument(form: RunForm): CompiledDocument {
  const audience = form.recipientName
    ? `${form.recipientName}, ${form.recipientRole}, ${form.organization}`
    : `${form.recipientRole}, ${form.organization}`;
  const sourceTitle = form.sourceConcept?.title || "ATLAS DPI Concept Note";
  const sourceMode = form.sourceConcept?.mode === "workspace" ? "workspace source" : "canonical source";

  const sections: DocumentSection[] = [
    {
      id: "opening-frame",
      title: "Opening frame",
      body: `For ${form.organization}, ATLAS can be introduced as a governed medical AI evaluation initiative: independent, evidence-led, and built for accountable deployment without changing the underlying proposal.`
    },
    {
      id: "audience-fit",
      title: "Audience fit",
      // The funding figure and the country-node count are declared protected facts (see
      // protectedFacts below). Surfacing them INSIDE an editable body — rather than only in a
      // separate list — is what lets the editor demonstrate the product's central promise: the
      // operator can see, live, which words in the text they are typing over are sealed and
      // will not be rewritten. Deleting one removes its chip.
      body: `The strongest fit is the shared need for trustworthy clinical evidence, sober AI governance, and implementation discipline across 9 country nodes. The document should make that fit visible without implying endorsement, partnership, or that the US$6.5M-$25M over 36 months request is secured.`
    },
    {
      id: "protected-position",
      title: "Protected position",
      body: "The funding request, phasing, country-node status, citations, and certainty language remain unchanged. Any substantive change stays outside automatic editing and must be flagged for review.",
      locked: true
    },
    {
      id: "next-step",
      title: "Next step",
      body: `The closing should ask for a focused exploratory conversation with ${form.organization} on catalytic financing, clinical safety evidence, and non-extractive governance.`
    }
  ];

  return {
    title: `${form.organization} audience draft`,
    subtitle: `${sourceTitle} personalization package`,
    audience,
    updatedAt: new Date().toISOString(),
    designNotes: [
      `Source: ${sourceTitle} (${sourceMode}).`,
      "Use short blocks with clear section names.",
      "Keep protected numbers and claims stable.",
      "Prefer restrained Meridian language over broad promotion."
    ],
    // LOCAL-PREVIEW ILLUSTRATION ONLY. No validator reads this array — V1 checks the pinned claims
    // register on the backend. It exists so local preview (apiUrl === "") can show the sealed-chip
    // affordance against two real document facts. "Human approval before export" was REMOVED: it is a
    // description of policy, not a fact in the document, and sealing it told the operator a token was
    // governed when no validator covers it. A remote run gets protectedFacts from the register on the
    // wire (run.document.protectedFacts), never from here.
    protectedFacts: ["US$6.5M-$25M over 36 months", "9 country nodes"],
    sections: applyApprovedChangesToSections(sections, form.plannedChanges)
  };
}

export function createEmptyDocument(): CompiledDocument {
  return {
    title: "",
    subtitle: "",
    audience: "",
    updatedAt: new Date().toISOString(),
    sections: [],
    designNotes: [],
    protectedFacts: []
  };
}

export function createChanges(form: RunForm): ChangeItem[] {
  if (form.plannedChanges?.length) return finalizePlannedChanges(form.plannedChanges);
  return [
    {
      id: "chg-001",
      page: "Page 2",
      title: "Opening frame",
      before: "ATLAS is presented as a sovereign medical AI evaluation initiative.",
      after: `ATLAS is framed for ${form.organization} through independent, evidence-led health AI evaluation.`,
      reason: "Audience alignment without changing the proposal.",
      verdict: "pending"
    },
    {
      id: "chg-002",
      page: "Page 7",
      title: "Why now",
      before: "The note describes momentum across evaluation, safety, and country-node readiness.",
      after: "The note emphasizes accountable funding, clinical safety, and non-extractive governance.",
      reason: "Sharper relevance with the same factual base.",
      verdict: "pending"
    },
    {
      id: "chg-003",
      page: "Page 31",
      title: "Next step",
      before: "The closing asks for an exploratory conversation about catalytic financing.",
      after: "The closing asks for a focused exploratory conversation on catalytic financing and evidence standards.",
      reason: "Clearer call-to-action without substantive commitments.",
      verdict: "pending"
    }
  ];
}

/**
 * SAFETY: only APPROVED changes may touch section bodies, and a locked (protected) section
 * is never rewritten, whatever the verdict says.
 */
export function applyApprovedChangesToSections(sections: DocumentSection[], changes: ChangeItem[] = []) {
  const approved = changes.filter((change) => change.verdict === "approved");
  if (!approved.length) return sections;
  return sections.map((section) => {
    if (section.locked) return section;
    const match = approved.find((change) => changeTargetsSection(change, section));
    return match ? { ...section, body: match.after } : section;
  });
}

export function changeTargetsSection(change: ChangeItem, section: DocumentSection) {
  const target = `${change.id} ${change.title}`.toLowerCase();
  const sectionName = `${section.id} ${section.title}`.toLowerCase();
  return target.includes(section.id.toLowerCase()) || sectionName.includes(change.title.toLowerCase());
}

export function applyLocalPrompt(run: RunStatus, prompt: string, document: CompiledDocument): RunStatus {
  const updated = {
    ...document,
    updatedAt: new Date().toISOString(),
    sections: document.sections.map((section, index) =>
      index === 0 && !section.locked
        ? {
            ...section,
            body: `${section.body}\n\nEditor note applied: ${prompt}`
          }
        : section
    )
  };

  return {
    ...run,
    status: "needs_review",
    document: updated,
    message: "Prompt edit applied to draft.",
    promptHistory: [
      {
        prompt,
        result: "Applied to opening frame for local preview.",
        createdAt: new Date().toISOString()
      },
      ...run.promptHistory
    ],
    changes: [
      {
        id: `chg-${Date.now()}`,
        page: "Editor",
        title: "Prompt edit",
        before: "Previous draft language",
        after: prompt,
        reason: "Operator-requested live edit.",
        verdict: "pending"
      },
      ...run.changes
    ]
  };
}

/**
 * NORMALIZE A REMOTE RUN — WITHOUT INVENTING ANYTHING.
 *
 * This used to spread `createDemoRun(form)` UNDERNEATH the backend response and fall back to
 * `createChanges(form)` whenever the backend proposed no edits — so a run the backend created with
 * zero changes rendered THREE demo edits, and the operator reviewed and approved fictions. Combined
 * with the deleted applyPlannedChangesToRun (which overwrote the backend's real patches with the
 * client's plannedChanges), the reviewed change set had nothing to do with the change set POST
 * /approve validated. That is the fabrication this workstream exists to remove.
 *
 * The rule now: a remote run renders the BACKEND'S run or nothing. The backend returns a complete,
 * frozen RunStatus; this function only guarantees the required arrays/objects EXIST as empty-safe
 * defaults so the UI cannot crash on a malformed response. It invents no changes, no document
 * content, and no protected facts. If the backend proposed no edits, the operator sees no edits.
 */
export function normalizeRun(data: RunStatus, form: RunForm): RunStatus {
  return {
    ...data,
    input: data.input || form,
    document: data.document || createEmptyDocument(),
    changes: Array.isArray(data.changes) ? data.changes : [],
    stages: Array.isArray(data.stages) ? data.stages : [],
    promptHistory: Array.isArray(data.promptHistory) ? data.promptHistory : []
  };
}
